import { Link } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import { CATEGORIES } from "@/data/campaigns";

/**
 * Maps a category badge label → tailwind palette classes.
 * Keeps trust language visually consistent across surfaces.
 */
const badgeTone = (label) => {
  switch (label) {
    case "Early Access":
      return "bg-saffron-500/15 text-saffron-700 border-saffron-500/30";
    case "Coming Soon":
      return "bg-navy-800/8 text-navy-700 border-navy-800/15";
    case "Launching Soon":
      return "bg-forest-500/15 text-forest-700 border-forest-500/25";
    case "Be the First Creator":
      return "bg-saffron-500/15 text-saffron-700 border-saffron-500/30";
    case "Join Early Access":
      return "bg-navy-800 text-white border-navy-800";
    default:
      return "bg-warm-100 text-navy-700 border-warm-200";
  }
};

const Categories = () => {
  return (
    <div data-testid="categories-page">
      <section className="border-b border-warm-200 bg-cream-100">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-saffron-500/30 bg-saffron-500/10 px-3 py-1 text-xs font-semibold text-saffron-700">
              <Sparkles className="h-3.5 w-3.5" /> Now in early access
            </span>
            <h1 className="mt-4 font-display text-4xl md:text-6xl font-bold tracking-tight text-navy-800">Every idea. Every industry.</h1>
            <p className="mt-4 text-lg text-navy-600 leading-[1.7]">
              Ten curated categories opening in phases. Reserve your category and be first in line when Dwinov opens for creators.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/#early-access"
                className="btn-saffron inline-flex items-center rounded-2xl h-11 px-5 font-semibold"
                data-testid="categories-cta-early-access"
              >
                Register &amp; Join early access <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
              <Link
                to="/about"
                className="btn-navy-outline border-2 inline-flex items-center rounded-2xl h-11 px-5 font-semibold"
              >
                Learn more
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 auto-rows-[200px] md:auto-rows-[240px]">
          {CATEGORIES.map((c, i) => {
            const Icon = c.icon;
            const sizeCls = [0, 5].includes(i) ? "md:col-span-2" : [3, 8].includes(i) ? "md:row-span-2" : "";
            const tone = badgeTone(c.badge.label);
            return (
              <Link
                key={c.id}
                to={`/explore?category=${c.id}`}
                data-testid={`category-card-${c.id}`}
                className={`group relative rounded-[20px] overflow-hidden bg-navy-800 shadow-warm hover:shadow-warm-lg hover:scale-[1.01] transition-[transform,box-shadow] duration-500 ${sizeCls}`}
              >
                <img
                  src={c.image} alt={c.name} loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover opacity-60 group-hover:opacity-70 group-hover:scale-105 transition-all duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-900/95 via-navy-900/50 to-transparent" />
                <div className="relative h-full p-6 flex flex-col justify-between">
                  <div className={`inline-grid place-items-center h-11 w-11 rounded-xl bg-gradient-to-br ${c.color} text-white shadow-md`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="font-display text-2xl font-bold text-white tracking-tight">{c.name}</div>
                    <div className="mt-2 flex items-center justify-between gap-3">
                      <span
                        data-testid={`category-badge-${c.id}`}
                        className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold backdrop-blur-sm ${tone}`}
                      >
                        <span aria-hidden>{c.badge.emoji}</span>
                        {c.badge.label}
                      </span>
                      <ArrowRight className="h-4 w-4 text-white/70 group-hover:translate-x-1 group-hover:text-white transition-all" />
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>
    </div>
  );
};

export default Categories;
